<template>
  <div>
    <ts-board class="header-board">
      <div class="header-board-container">
        <span class="header-board-title">
          Export
          <b>{{ tenant.CompanyName }}</b>
          Configuration
        </span>
        <ts-action-select
          :items="configFileTypes"
          @action-select-click="exportConfig"
          testid="exportConfigButton"
        >
          <ts-button type="primary" :busy="loading">
            Export Configuration
          </ts-button>
        </ts-action-select>
      </div>
    </ts-board>
    <ts-board v-if="config" class="config-board" data-title="Configuration">
      <!-- eslint-disable-next-line vue/no-deprecated-slot-attribute -->
      <div slot="header-actions">
        <ts-button
          testid="viewReportButton"
          class="view-report-btn"
          type="text"
          icon="categories"
          @click="viewReport"
        >
          View Report
        </ts-button>
        <ts-button
          testid="downloadConfigButton"
          type="text"
          icon="download"
          @click="download"
        >
          Download
        </ts-button>
        <ts-button
          testid="copyConfigButton"
          type="text"
          icon="all-documents"
          @click="copyToClipboard"
        >
          Copy to Clipboard
        </ts-button>
      </div>
      <div v-if="encounteredError" class="alert alert-danger" role="alert">
        <div class="row justify-content-between">
          <div>
            <h4 class="alert-heading">
              Alert! This is a partially exported configuration of this company!
            </h4>
            <p>
              We encountered some issues and created a report that includes the
              error details.
            </p>
          </div>
          <ts-button
            testid="viewReportButtonInAlert"
            type="danger"
            @click="viewReport"
          >
            View Report
          </ts-button>
        </div>
        <hr />
        <div class="row">
          <p class="mb-0">
            We recommend that you try the export again after a while. If the
            issue persists but you need to move on with migrating this company
            to a new environment, then you can use the bellow partial config
            file to create at least a part of the company via Automated
            Configuration.
          </p>
        </div>
      </div>
      <pre>{{ config }}</pre>
    </ts-board>

    <a :href="fileData" :download="fileName" ref="downloadLink"></a>
  </div>
</template>

<script>
import '@tradeshift/elements.action-select';
import '@tradeshift/elements.board';
import '@tradeshift/elements.button';
import '@tradeshift/elements.spinner';
import { dump as yamlSerializer, load as yamlLoader } from 'js-yaml';

import { getCompanyConfig } from '../../../api';
import { SUPPORTED_CONFIG_FILE_TYPES } from '../../../constants';
import { notify } from '../../../utils';

export default {
  props: {
    tenant: { type: Object, default: null },
  },
  emits: ['open-report'],
  data() {
    return {
      config: null,
      loading: false,
      taskId: null,
      failedToExport: false,
      configFileType: 'json',
      configFileTypes: [
        {
          id: SUPPORTED_CONFIG_FILE_TYPES.JSON,
          title: 'Export Configuration as JSON',
        },
        {
          id: SUPPORTED_CONFIG_FILE_TYPES.YAML,
          title: 'Export Configuration as YAML',
        },
      ],
    };
  },
  computed: {
    configContent() {
      return this.configFileType === SUPPORTED_CONFIG_FILE_TYPES.JSON
        ? JSON.stringify(this.config)
        : this.config;
    },
    fileData() {
      return `data:text/${
        this.configFileType
      };charset=utf-8,${encodeURIComponent(this.configContent)}`;
    },
    fileName() {
      const companyName = this.tenant ? this.tenant.CompanyName : 'company';
      const fileType =
        this.configFileType === SUPPORTED_CONFIG_FILE_TYPES.YAML
          ? 'yaml'
          : 'json';
      return `${companyName}_config_file.${fileType}`;
    },
    encounteredError() {
      if (this.failedToExport && typeof this.failedToExport === 'object') {
        return Object.keys(this.failedToExport).length > 0;
      }
      return false;
    },
  },
  methods: {
    exportConfig(e) {
      const fileType = e.detail.selected;
      this.configFileType = fileType;
      this.loading = true;
      getCompanyConfig(this.tenant.CompanyAccountId, fileType)
        .then(this.handleExportedConfig)
        .finally(() => {
          this.loading = false;
        });
    },
    handleExportedConfig(data) {
      let fileData = data;
      if (this.configFileType === SUPPORTED_CONFIG_FILE_TYPES.YAML) {
        fileData = yamlLoader(data);
        this.config = yamlSerializer(fileData.config);
      } else {
        this.config = fileData.config;
      }
      this.taskId = fileData.taskId;
      this.failedToExport = fileData.failedToExport;
    },
    download() {
      this.$refs.downloadLink.click();
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.configContent).then(
        () => {
          notify.succeeded('Copied to clipboard!');
        },
        (err) => {
          notify.failed(
            err,
            'Encountered an issue while copying the configuration file to clipboard:'
          );
        }
      );
    },
    viewReport() {
      this.$emit('open-report', this.taskId);
    },
  },
};
</script>

<style scoped>
.header-board {
  margin-bottom: var(--ts-unit);
}

.header-board-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-board-title {
  font-size: var(--ts-fontsize-big);
}

header {
  width: 100%;
  padding: var(--ts-unit-half) var(--ts-unit);
  text-align: right;
}

.config-board {
  background-color: var(--ts-color-white);
}

pre {
  border: 1px solid var(--ts-color-gray-light);
  background-color: var(--ts-color-gray-lightest);
  padding: var(--ts-unit-quarter) var(--ts-unit-half);
  max-height: 500px;
  overflow: auto;
}

.view-report-btn {
  position: relative;
  margin-inline-end: var(--ts-unit-half);
  padding-inline-end: var(--ts-unit-half);
}

.view-report-btn:after {
  content: '';
  display: inline-block;
  position: absolute;
  left: 100%;
  top: var(--ts-unit-half);
  height: var(--ts-unit);
  width: var(--ts-border-width);
  background-color: var(--ts-border-color-default);
}

.alert {
  padding: var(--ts-unit-plus);
}
</style>
